import { ContentType, PermissionEnum } from '@creditsights/api-ui-web';
import { RefObject, ReactElement } from 'react';
import { ICONS, ActionButton, Divider } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { NavLink } from 'react-router-dom';
import { Article } from '../../../models';
import { hasPermissionToDownload, hasPermissionToPrint, hasPermissionToShare } from '../../../utils/permissionsUtils';
import styles from './ArticleActions.module.scss';
import useGetArticleActions from '../../../hooks/articles/useGetArticleActions';
import { getArticleDataTestId } from '../../../components/ArticlePreview/ArticlePreview.logic';
import useFetchUserPermissions from '../../../hooks/permissions/useFetchUserPermissions';

const cx = classNames.bind(styles);

export interface ArticleActionsProps {
  article: Article;
  isContentReadyForPrint: boolean;
  isArticlePreview?: boolean;
  articlePrintRef?: RefObject<HTMLDivElement>;
  isCompact?: boolean;
  isAnalystWidgetLoading?: boolean;
}

const ArticleActions = ({
  article,
  isArticlePreview,
  articlePrintRef,
  isCompact,
  isContentReadyForPrint,
  isAnalystWidgetLoading,
}: ArticleActionsProps): ReactElement => {
  const { data: userPermissions } = useFetchUserPermissions();
  const {
    onShare,
    isBookmarkLoading,
    onBookmark,
    bookmarkId,
    onLike,
    isLikeLoading,
    likeId,
    onDownload,
    onPrint,
    isDownloadLoading,
    isPrintLoading,
    onEdit,
  } = useGetArticleActions({
    id: article.id,
    title: article.content.title,
    content: article.content,
    tags: article.tags,
    publishDate: article.publishDate,
    articlePrintRef: articlePrintRef?.current,
    canAccess: article.canAccess,
  });

  const canEdit =
    article.articleType === ContentType.Research &&
    article.id >= 300000 &&
    userPermissions?.permissions.includes(PermissionEnum.STAFF);
  const canPrint = hasPermissionToPrint(userPermissions);
  const canDownload = hasPermissionToDownload(userPermissions!);
  const canShare = hasPermissionToShare(userPermissions!);

  return (
    <div className={cx('body')} data-testid="article-actions-view">
      <div className={cx('left-actions')}>
        <ActionButton
          icon={bookmarkId ? ICONS.BookmarkFilled : ICONS.Bookmark}
          onClick={onBookmark}
          isLoading={isBookmarkLoading}
          tooltip={bookmarkId ? 'Remove from My Content' : 'Save to My Content'}
          dataTestId={getArticleDataTestId('on-bookmark-button', !!isArticlePreview)}
          ariaLabel={bookmarkId ? 'Unbookmark' : 'Bookmark'}
          disabled={!article.canAccess}
        />
        <ActionButton
          icon={ICONS.Download}
          onClick={() => onDownload()}
          isLoading={isDownloadLoading || !isContentReadyForPrint || isAnalystWidgetLoading}
          tooltip={canDownload ? 'Download' : 'Upgrade Your Subscription to Download'}
          ariaLabel="Download"
          dataTestId={getArticleDataTestId('on-download-button', !!isArticlePreview)}
          disabled={!article.canAccess || !canDownload}
          showTooltipWhenDisabled={article.canAccess}
        />
        <ActionButton
          icon={ICONS.Print}
          onClick={() => onPrint()}
          isLoading={isPrintLoading || !isContentReadyForPrint || isAnalystWidgetLoading}
          tooltip={canPrint ? 'Print' : 'Upgrade Your Subscription to Print'}
          dataTestId={getArticleDataTestId('on-print-button', !!isArticlePreview)}
          ariaLabel="Print"
          disabled={!article.canAccess || !canPrint}
          showTooltipWhenDisabled={article.canAccess}
        />
        {canShare && (
          <ActionButton
            icon={ICONS.Share}
            onClick={onShare}
            tooltip="Share"
            dataTestId={getArticleDataTestId('on-share-button', !!isArticlePreview)}
            ariaLabel="Share"
            disabled={!article.canAccess}
          />
        )}
        {canEdit && (
          <ActionButton
            icon={ICONS.Edit}
            onClick={onEdit}
            tooltip="Edit"
            dataTestId={getArticleDataTestId('on-edit-button', !!isArticlePreview)}
            ariaLabel="Edit"
            disabled={!article.canAccess}
          >
            Edit
          </ActionButton>
        )}
      </div>
      <div className={cx('right-actions')}>
        {(isCompact || isArticlePreview) && article.canAccess && (
          <NavLink to={`/articles/${article.id}`} className={cx('view-full-article')} data-testid="view-full-article">
            Full Article
          </NavLink>
        )}
        {!isArticlePreview && (
          <>
            <Divider vertical className={cx('divider')} />
            <ActionButton
              icon={ICONS.Like}
              onClick={onLike}
              isLoading={isLikeLoading}
              tooltip={likeId ? 'Unlike article' : 'Did you find this article helpful?'}
              dataTestId={likeId ? 'on-unlike-button' : 'on-like-button'}
              ariaLabel="Like"
              selected={!!likeId}
              disabled={!article.canAccess}
            >
              Like
            </ActionButton>
          </>
        )}
      </div>
    </div>
  );
};

export default ArticleActions;
